import {z} from 'zod';
import {
	editRemovesFromFilter,
	type EditRemovesFromFilterParams,
	getDefaultFilters,
	type GetDefaultFiltersParams,
	makeOptionalPropsNullable,
} from '@netx/core-schema';
import {
	CustomActionField,
	type ICustomAction,
	CustomActionSchema,
} from './customActionsSchema';

export const CustomActionFilterSchema = makeOptionalPropsNullable(
	CustomActionSchema.partial()
		.pick({
			[CustomActionField.name]: true,
			[CustomActionField.payloadUrl]: true,
			[CustomActionField.targetUrl]: true,
		})
		.extend({
			isEnabled: z.boolean().optional(),
		}),
);

export const CustomActionsFilterableField =
	CustomActionFilterSchema.keyof().enum;

export const CUSTOM_ACTIONS_FILTERABLE_FIELDS = Object.values(
	CustomActionsFilterableField,
);

export type CustomActionsFilter = z.infer<typeof CustomActionFilterSchema>;

/** will return the filters passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getCustomActionsDefaultFilters = (
	params?: Omit<
		GetDefaultFiltersParams<CustomActionsFilter>,
		'filterableFields'
	>,
): CustomActionsFilter =>
	getDefaultFilters({
		...params,
		filterableFields: CUSTOM_ACTIONS_FILTERABLE_FIELDS,
	});

/** given the original data, the new data, and the current filters, determine if the record should still be included in the filtered list */
export const customActionEditRemovesFromFilter = (
	params: Omit<
		EditRemovesFromFilterParams<ICustomAction, CustomActionsFilter>,
		'schema'
	>,
) => {
	const updateNeeded = editRemovesFromFilter({
		...params,
		schema: CustomActionSchema,
	});
	if (updateNeeded) return true;
	if (params.filters.isEnabled == null) return updateNeeded;
	// this is a faux filter field and does not actually directly match any props on the data object
	// in this case though I am not sure why we went with `isEanabled` other than it was cruft carried
	// over from webbooks where we we have two types of "disabled" states
	return params.filters.isEnabled !== params.updatedData.enabled;
};
